import { ResourceQuota } from 'kubernetes-types/core/v1';
import { useState } from 'react';

import { ResourceUsageBar } from '@/react/kubernetes/components/ResourceUsageBar';

import { Button } from '@@/buttons';

import { usageRatio } from './quantity';

/** Limits shown before the rest go behind the more button. */
const VISIBLE_LIMITS = 4;

/**
 * The limits people read a quota for, ahead of the rest, so the entries a row
 * has room for are the useful ones.
 */
const PROMINENT_LIMITS = [
  'limits.cpu',
  'limits.memory',
  'requests.cpu',
  'requests.memory',
  'pods',
];

export function UsedOfHardCell({ quota }: { quota: ResourceQuota }) {
  const [showAll, setShowAll] = useState(false);
  const limits = usedOfHardEntries(quota);

  if (limits.length === 0) {
    return <span className="text-muted">-</span>;
  }

  const visible = showAll ? limits : limits.slice(0, VISIBLE_LIMITS);
  const hidden = limits.length - visible.length;

  return (
    <div className="flex flex-col gap-1">
      {visible.map(({ resource, used, hard }) => (
        <ResourceUsageBar
          key={resource}
          label={resource}
          labelClassName="w-56"
          value={Math.round(usageRatio(used, hard) * 100)}
          total={100}
          annotation={`${used} / ${hard}`}
          className="mt-0"
          /* bootstrap-override.css sets .progress to 50% of its container at
             the same specificity as a Tailwind width, so the width is forced */
          barClassName="!w-24 shrink-0"
        />
      ))}
      {hidden > 0 && (
        <Button
          color="link"
          onClick={() => setShowAll(true)}
          className="!ml-0 w-fit pl-0"
          data-cy={`show-all-limits-${quota.metadata?.name}`}
        >
          +{hidden} more
        </Button>
      )}
    </div>
  );
}

/** Every hard limit on the quota, paired with what it has used. */
export function usedOfHardEntries(quota: ResourceQuota) {
  const hard = quota.status?.hard ?? {};
  const used = quota.status?.used ?? {};

  return Object.keys(hard)
    .sort(byProminenceThenName)
    .map((resource) => ({
      resource,
      hard: hard[resource],
      used: used[resource] ?? '0',
    }));
}

function byProminenceThenName(a: string, b: string) {
  const rankA = PROMINENT_LIMITS.indexOf(a);
  const rankB = PROMINENT_LIMITS.indexOf(b);

  if (rankA !== rankB) {
    return (
      (rankA < 0 ? PROMINENT_LIMITS.length : rankA) -
      (rankB < 0 ? PROMINENT_LIMITS.length : rankB)
    );
  }

  return a.localeCompare(b);
}
